import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { productService } from '../../services/productService';
import { ProductCard } from './ProductCard';
import { ProductFormModal } from './ProductFormModal';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { copyProductLink } from '../../utils/deepLinks';
import type { Product } from '../../types/product';
import type { Facility } from '../../types/facility';

interface ProductsSectionProps {
  businessProfileId?: string;
  facilities?: Facility[];
  isCollapsed?: boolean;
  onToggle?: () => void;
}

export function ProductsSection({
  businessProfileId,
  facilities = [],
  isCollapsed = false,
  onToggle,
}: ProductsSectionProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadProducts = useCallback(async () => {
    if (!token || !businessProfileId) return;

    setIsLoading(true);
    try {
      const data = await productService.getProducts(businessProfileId, token);
      setProducts(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load products';
      showToast('error', t('common.error'), message);
    } finally {
      setIsLoading(false);
    }
  }, [token, businessProfileId, showToast, t]);

  // Load data on mount to show count in header
  useEffect(() => {
    if (businessProfileId) {
      loadProducts();
    }
  }, [businessProfileId, loadProducts]);

  const handleAdd = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleDelete = (product: Product) => {
    setDeletingProduct(product);
  };

  const handleCopyLink = async (product: Product) => {
    const ok = await copyProductLink(product.id);
    if (ok) {
      showToast('success', t('common.linkCopied'));
    } else {
      showToast('error', t('common.linkCopyError'));
    }
  };

  const confirmDelete = async () => {
    if (!token || !businessProfileId || !deletingProduct) return;

    setIsDeleting(true);
    try {
      await productService.deleteProduct(businessProfileId, deletingProduct.id, token);
      showToast('success', t('products.deleteSuccess'));
      setProducts(prev => prev.filter(p => p.id !== deletingProduct.id));
      setDeletingProduct(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete product';
      showToast('error', t('common.error'), message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
    loadProducts();
  };

  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-brand-confirm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <span className="text-lg font-medium text-white">{t('products.title')}</span>
          <span className="text-sm text-white/70">({products.length})</span>
        </div>
        <svg
          className={`w-5 h-5 text-white/70 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-4">
          {/* Add Button */}
          <button
            onClick={handleAdd}
            disabled={!businessProfileId}
            className="w-full py-3 border-2 border-dashed border-white/30 rounded-lg text-white/80 hover:border-brand-confirm hover:text-brand-confirm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('products.addProduct')}
          </button>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white/10 rounded-lg p-4 animate-pulse">
                  <div className="h-5 bg-white/20 rounded w-48 mb-2" />
                  <div className="h-4 bg-white/20 rounded w-32" />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && products.length === 0 && (
            <div className="text-center py-8 text-white/60">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p>{t('products.noProducts')}</p>
            </div>
          )}

          {/* Products List */}
          {!isLoading && products.length > 0 && (
            <div className="space-y-3">
              {products.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => handleEdit(product)}
                  onDelete={() => handleDelete(product)}
                  onCopyLink={() => handleCopyLink(product)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && businessProfileId && (
        <ProductFormModal
          product={editingProduct}
          businessProfileId={businessProfileId}
          facilities={facilities}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingProduct && (
        <ConfirmationModal
          title={t('products.deleteConfirmTitle')}
          message={t('products.deleteConfirmMessage', { name: deletingProduct.title })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={confirmDelete}
          onCancel={() => setDeletingProduct(null)}
          isLoading={isDeleting}
          variant="danger"
        />
      )}
    </div>
  );
}
